import type { ThreadAttachment } from '@zuruflex/types';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { ChevronLeft, ChevronRight, Download, FileText, ImageOff, X } from 'lucide-react';
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/cn.js';

/**
 * Pictures and files in a conversation, for a browser.
 *
 * The one thing a browser cannot do here is the obvious thing: point an
 * `<img>` or an `<a>` at `attachment.url`. That is a route on the API, and
 * the API wants a bearer token an element cannot carry — so the old links
 * answered 401 and a photograph was a filename. The way in is the same one
 * the phones use, `GET /v1/threads/attachments/:id/link`, which answers a
 * signed URL in a body. The design system holds no API client, so each app
 * hands the call in as `resolveAttachment` and this file asks it.
 */

/** Asks the API for a short-lived URL to one attachment. */
export type ResolveAttachment = (attachmentId: string) => Promise<string>;

const ResolverContext = createContext<ResolveAttachment | undefined>(undefined);

export function AttachmentResolverProvider({
  resolve,
  children,
}: {
  resolve: ResolveAttachment | undefined;
  children: ReactNode;
}) {
  return <ResolverContext.Provider value={resolve}>{children}</ResolverContext.Provider>;
}

/**
 * Links minted, by attachment, with when they were minted.
 *
 * Module-wide rather than per bubble, so a photo that appears in the grid
 * and then in the lightbox is asked for once. Reused for fifty seconds of
 * the sixty a signature lasts — the margin is for the request that starts
 * at second fifty-nine. An `<img>` that has already loaded keeps its pixels
 * after the signature expires, so a stale entry costs only a re-ask.
 */
const minted = new Map<string, { url: string; at: number }>();
const inFlight = new Map<string, Promise<string>>();
const FRESH_MS = 50_000;

function linkFor(id: string, resolve: ResolveAttachment): Promise<string> {
  const hit = minted.get(id);
  if (hit && Date.now() - hit.at < FRESH_MS) return Promise.resolve(hit.url);

  const pending = inFlight.get(id);
  if (pending) return pending;

  const request = resolve(id)
    .then((url) => {
      minted.set(id, { url, at: Date.now() });
      return url;
    })
    .finally(() => inFlight.delete(id));
  inFlight.set(id, request);
  return request;
}

/**
 * A URL an element can load for this attachment, or undefined while one is
 * being fetched. `null` means there is no way to get one: no resolver was
 * handed in, or the API refused.
 */
function useAttachmentSrc(file: ThreadAttachment, prefer: 'thumbnail' | 'original') {
  const resolve = useContext(ResolverContext);
  const direct = prefer === 'thumbnail' ? file.thumbnailUrl : null;
  const [src, setSrc] = useState<string | null | undefined>(direct ?? undefined);

  useEffect(() => {
    if (direct) {
      setSrc(direct);
      return;
    }
    // A blob: or data: URL is already loadable — a message still on its way.
    if (/^(blob|data):/.test(file.url)) {
      setSrc(file.url);
      return;
    }
    if (!resolve) {
      setSrc(null);
      return;
    }
    let live = true;
    setSrc(undefined);
    linkFor(file.id, resolve).then(
      (url) => live && setSrc(url),
      () => live && setSrc(null),
    );
    return () => {
      live = false;
    };
  }, [direct, file.id, file.url, resolve]);

  return src;
}

export function isImage(file: Pick<ThreadAttachment, 'contentType'>): boolean {
  return file.contentType.startsWith('image/');
}

/**
 * Pictures, laid out as pictures: one fills the width, several tile, a
 * fifth and beyond is a count on the fourth. A click opens the lightbox at
 * that picture.
 */
export function PhotoGrid({
  images,
  onOpen,
  overlay,
}: {
  images: ThreadAttachment[];
  onOpen: (index: number) => void;
  /** Laid over the bottom corner — the time, on a message with no words. */
  overlay?: ReactNode;
}) {
  const { t } = useTranslation('common');
  const single = images.length === 1;
  const shown = images.slice(0, 4);

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-[1.125rem]',
        single ? 'w-80 max-w-full' : 'grid w-80 max-w-full grid-cols-2 gap-0.5',
      )}
    >
      {shown.map((file, index) => (
        <button
          key={file.id}
          type="button"
          onClick={() => onOpen(index)}
          aria-label={t('messaging.photoOf', { number: index + 1, total: images.length })}
          className={cn(
            'relative block overflow-hidden bg-surface-sunken outline-none focus-visible:ring-2 focus-visible:ring-accent',
            single ? 'aspect-[4/3]' : 'aspect-square',
            images.length === 3 && index === 0 && 'col-span-2 aspect-[2/1]',
          )}
        >
          <Thumb file={file} />
          {index === 3 && images.length > 4 ? (
            <span className="absolute inset-0 grid place-items-center bg-overlay text-xl font-semibold text-white">
              +{images.length - 4}
            </span>
          ) : null}
        </button>
      ))}
      {overlay ? (
        <span className="pointer-events-none absolute bottom-2 right-2 rounded-pill bg-black/55 px-2 py-1 [&_*]:!text-white">
          {overlay}
        </span>
      ) : null}
    </div>
  );
}

function Thumb({ file }: { file: ThreadAttachment }) {
  const { t } = useTranslation('common');
  const src = useAttachmentSrc(file, 'thumbnail');
  const [broken, setBroken] = useState(false);

  if (src === null || broken) {
    return (
      <span
        className="grid size-full place-items-center text-text-subtle"
        title={t('messaging.imageFailed')}
      >
        <ImageOff className="size-5" />
      </span>
    );
  }
  if (src === undefined) return <span className="block size-full animate-pulse bg-surface-hover" />;

  return (
    <img
      src={src}
      alt={t('messaging.photo')}
      loading="lazy"
      onError={() => setBroken(true)}
      className="size-full object-cover transition-transform duration-300 hover:scale-[1.03]"
    />
  );
}

/**
 * One attached file that is not a picture: an icon, its name and its size.
 *
 * Opened by asking for a link at the click, not before — a thread with ten
 * PDFs in it should not mint ten signatures nobody uses. The tab is opened
 * first and pointed afterwards, because a window opened after an `await`
 * is a popup and gets blocked.
 */
export function AttachmentChip({ file, onAccent }: { file: ThreadAttachment; onAccent: boolean }) {
  const resolve = useContext(ResolverContext);

  const open = useCallback(() => {
    if (!resolve) return;
    const tab = window.open('', '_blank');
    linkFor(file.id, resolve).then(
      (url) => {
        if (tab) tab.location.href = url;
        else window.location.href = url;
      },
      () => tab?.close(),
    );
  }, [file.id, resolve]);

  return (
    <button
      type="button"
      onClick={open}
      disabled={!resolve}
      className={cn(
        'flex w-full min-w-56 items-center gap-2.5 rounded-[0.75rem] px-2.5 py-2 text-left text-xs transition-colors',
        onAccent ? 'bg-accent-text/10 hover:bg-accent-text/15' : 'bg-surface/60 hover:bg-surface',
      )}
    >
      <span
        className={cn(
          'grid size-9 shrink-0 place-items-center rounded-[0.5rem]',
          onAccent ? 'bg-accent-text/10' : 'bg-surface-hover',
        )}
      >
        <FileText className="size-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium">{file.filename}</span>
        <span className="block opacity-70">
          {fileKind(file)} · {formatBytes(file.sizeBytes)}
        </span>
      </span>
      <Download className="size-4 shrink-0 opacity-60" />
    </button>
  );
}

/** `PDF`, `DOCX` — what the extension says, since that is what people read. */
export function fileKind(file: Pick<ThreadAttachment, 'filename' | 'contentType'>): string {
  const ext = file.filename.split('.').pop();
  if (ext && ext !== file.filename && ext.length <= 5) return ext.toUpperCase();
  return file.contentType.split('/').pop()?.toUpperCase() ?? '';
}

/** `1.4 MB`. Decimal units, because that is what a phone's gallery says. */
export function formatBytes(bytes: number): string {
  if (bytes < 1000) return `${bytes} B`;
  if (bytes < 1_000_000) return `${Math.round(bytes / 1000)} KB`;
  return `${(bytes / 1_000_000).toFixed(1)} MB`;
}

/**
 * A photograph, full size, over the conversation.
 *
 * In the page rather than in a new tab: looking at a picture of a dent
 * should not cost somebody the conversation about the dent. Arrow keys and
 * the side buttons step through the message's pictures; Escape, the close
 * button or a click on the backdrop puts it away.
 */
export function Lightbox({
  images,
  index,
  onIndexChange,
  onClose,
}: {
  images: ThreadAttachment[];
  index: number | null;
  onIndexChange: (index: number) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation('common');
  const open = index !== null;
  const current = open ? images[index] : undefined;
  const many = images.length > 1;

  const step = useCallback(
    (by: number) => {
      if (index === null) return;
      onIndexChange((index + by + images.length) % images.length);
    },
    [images.length, index, onIndexChange],
  );

  return (
    <DialogPrimitive.Root open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          aria-describedby={undefined}
          onKeyDown={(event) => {
            if (!many) return;
            if (event.key === 'ArrowRight') step(1);
            if (event.key === 'ArrowLeft') step(-1);
          }}
          className="fixed inset-0 z-50 flex flex-col outline-none"
        >
          <DialogPrimitive.Title className="sr-only">
            {t('messaging.photoOf', { number: (index ?? 0) + 1, total: images.length })}
          </DialogPrimitive.Title>

          <div className="flex items-center justify-between gap-4 px-4 py-3 text-white md:px-6">
            <p className="min-w-0 truncate text-sm text-white/80">
              {many
                ? t('messaging.photoOf', { number: (index ?? 0) + 1, total: images.length })
                : t('messaging.photo')}
            </p>
            <div className="flex items-center gap-1">
              {current ? <OriginalLink file={current} /> : null}
              <DialogPrimitive.Close
                aria-label={t('messaging.closePhoto')}
                className="grid size-10 place-items-center rounded-full text-white/80 transition-colors hover:bg-white/10 hover:text-white"
              >
                <X className="size-5" />
              </DialogPrimitive.Close>
            </div>
          </div>

          <div
            className="relative flex min-h-0 flex-1 items-center justify-center px-4 pb-6 md:px-20"
            // A click on the dark around the picture closes it; on the
            // picture itself it does not.
            onClick={(event) => event.target === event.currentTarget && onClose()}
          >
            {current ? <FullImage key={current.id} file={current} /> : null}

            {many ? (
              <>
                <StepButton side="left" onClick={() => step(-1)} />
                <StepButton side="right" onClick={() => step(1)} />
              </>
            ) : null}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function FullImage({ file }: { file: ThreadAttachment }) {
  const { t } = useTranslation('common');
  const src = useAttachmentSrc(file, 'original');
  const thumb = file.thumbnailUrl;
  const [loaded, setLoaded] = useState(false);
  const [broken, setBroken] = useState(false);

  if (src === null || broken) {
    return (
      <p className="flex items-center gap-2 text-sm text-white/70">
        <ImageOff className="size-5" />
        {t('messaging.imageFailed')}
      </p>
    );
  }

  return (
    <div className="relative flex max-h-full max-w-full items-center justify-center">
      {/* The small copy, blurred, while the original is on its way. */}
      {!loaded && thumb ? (
        <img
          src={thumb}
          alt=""
          className="max-h-[calc(100dvh-8rem)] max-w-full rounded-lg object-contain blur-sm"
        />
      ) : null}
      {!loaded && !thumb ? (
        <span className="size-10 animate-spin rounded-full border-2 border-white/20 border-t-white" />
      ) : null}
      {src ? (
        <img
          src={src}
          alt={t('messaging.photo')}
          onLoad={() => setLoaded(true)}
          onError={() => setBroken(true)}
          className={cn(
            'max-h-[calc(100dvh-8rem)] max-w-full rounded-lg object-contain shadow-2xl',
            !loaded && 'absolute opacity-0',
          )}
        />
      ) : null}
    </div>
  );
}

function OriginalLink({ file }: { file: ThreadAttachment }) {
  const src = useAttachmentSrc(file, 'original');
  if (!src) return null;
  return (
    <a
      href={src}
      target="_blank"
      rel="noreferrer"
      download={file.filename || undefined}
      className="grid size-10 place-items-center rounded-full text-white/80 transition-colors hover:bg-white/10 hover:text-white"
    >
      <Download className="size-5" />
    </a>
  );
}

function StepButton({ side, onClick }: { side: 'left' | 'right'; onClick: () => void }) {
  const Icon = side === 'left' ? ChevronLeft : ChevronRight;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-hidden
      tabIndex={-1}
      className={cn(
        'absolute top-1/2 grid size-11 -translate-y-1/2 place-items-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20',
        side === 'left' ? 'left-4 md:left-6' : 'right-4 md:right-6',
      )}
    >
      <Icon className="size-6" />
    </button>
  );
}
